import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { senvraApi } from "../utils/axiosInstance";

const ChangePassword = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = (values) => {
    let errors = {};
    if (!values.oldPassword) {
      errors.oldPassword = "Old password is required";
    }
    if (!values.newPassword) {
      errors.newPassword = "New password is required";
    } else if (values.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (values.confirmPassword !== values.newPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validate({ ...formData, [name]: value })[name],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setSuccessMessage("");

    const newErrors = validate(formData);
    setErrors(newErrors);
    setTouched({ oldPassword: true, newPassword: true, confirmPassword: true });

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
        
        if (!token) {
          setServerError("You must be logged in to change your password.");
          setIsSubmitting(false);
          return;
        }

        const response = await senvraApi.post('/auth/password/change', {
          old_password: formData.oldPassword,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setSuccessMessage(response.data?.message || "Password updated successfully");
        
        // Redirect back to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboards');
        }, 2000);

      } catch (error) {
        console.error('Change password error:', error);
        setServerError(error.response?.data?.message || error.response?.data?.detail || "Failed to update password.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div style={{ marginTop: "75px" }} className="w-full max-w-[480px]">
        {/* Top Icon */}
        <div className="flex justify-center mb-2">
          <div className="w-16 h-16 bg-[#0091ea] text-white rounded-2xl flex items-center justify-center shadow-lg shadow-sky-100">
            <KeyRound size={32} />
          </div>
        </div>

        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-['Libre_Baskerville'] text-[#1e293b] dark:text-white mb-2 tracking-tight">
            Change Password
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
            Enter your current and new password securely.
          </p>
        </div>

        {/* Success Message Toaster */}
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-2xl text-center text-sm font-bold">
            ✅ {successMessage}
          </div>
        )}

        {/* Error Alert Toaster */}
        {serverError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-center text-sm font-bold animate-shake">
            ⚠️ {serverError}
          </div>
        )}
        
        <div className="bg-white dark:bg-slate-900 rounded-[32px] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden relative p-8 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Old Password Field */}
            <div>
              <label className="block text-[12px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                Current Password
              </label>
              <div className="relative group">
                <Lock size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                <input
                  type={showPasswords.old ? "text" : "password"}
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-12 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                    touched.oldPassword && errors.oldPassword ? "ring-red-400" : "focus:ring-[#0091ea]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('old')}
                  className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600 transition"
                >
                  {showPasswords.old ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.oldPassword && errors.oldPassword && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <span>⚠️</span> {errors.oldPassword}
                </p>
              )}
            </div>

            {/* New Password Field */}
            <div>
              <label className="block text-[12px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                New Password
              </label>
              <div className="relative group">
                <Lock size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                <input
                  type={showPasswords.new ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-12 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                    touched.newPassword && errors.newPassword ? "ring-red-400" : "focus:ring-[#0091ea]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600 transition"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.newPassword && errors.newPassword && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <span>⚠️</span> {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-[12px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                Confirm New Password
              </label>
              <div className="relative group">
                <Lock size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-12 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                    touched.confirmPassword && errors.confirmPassword ? "ring-red-400" : "focus:ring-[#0091ea]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600 transition"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <span>⚠️</span> {errors.confirmPassword}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-4 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-full font-black text-base bg-[#0091ea] text-white transition-all hover:bg-[#0081d5] hover:shadow-xl hover:shadow-sky-100 disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="animate-spin" size={20} /> Updating...</>
                ) : (
                  "Update Password"
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)} // Navigates to the previous page they were on
                className="w-full py-3 rounded-full font-bold text-sm bg-transparent text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;